import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '../../../../components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';
import Pagination from '../../../../components/Pagination';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { Receipt } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { CompanyClaimTypeView, CompanyClaimTypeInput } from '../../../../models/payroll';
import { addCompanyClaimType, deleteCompanyClaimType, getCompanyClaimTypesWithStats, getNumberOfBeneficiariesOfClaimType } from '../../../../api/Payroll/claim';

const PAGE_SIZE = 10;

const ClaimsSettings: React.FC = () => {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [claimTypes, setClaimTypes] = useState<CompanyClaimTypeView[]>([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newClaimTypeName, setNewClaimTypeName] = useState('');
  const [selectedClaimType, setSelectedClaimType] = useState<{ id: number; name: string; beneficiaries: number } | null>(null);
  const [page, setPage] = useState(1);
  const [deleteBlockedMessage, setDeleteBlockedMessage] = useState<string | null>(null);

  const loadClaimTypes = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getCompanyClaimTypesWithStats();
      const list: CompanyClaimTypeView[] = (resp?.payload || []).map((c: any) => ({
        ...c,
        numberOfBeneficiaries: c.numberOfBeneficiaries || 0
      }));
      list.sort((a, b) => a.name.localeCompare(b.name));
      setClaimTypes(list);
    } catch (err) {
      console.log(err);
      showToast(t('Failed to load claim types'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showToast]);

  useEffect(() => { loadClaimTypes(); }, [loadClaimTypes]);

  const totalCost = useMemo(() => claimTypes.reduce((acc, c) => acc + (c.totalCost || 0), 0), [claimTypes]);

  const formatter = useMemo(() => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);

  const pagedClaimTypes = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return claimTypes.slice(start, start + PAGE_SIZE);
  }, [claimTypes, page]);

  const resetAddForm = () => { setNewClaimTypeName(''); };

  const handleAddClaimType = async () => {
    const name = newClaimTypeName.trim();
    if (!name) { showToast(t('Name is required'), 'error'); return; }
    if (claimTypes.some(c => c.name.toLowerCase() === name.toLowerCase())) { showToast(t('Name already exists'), 'error'); return; }
    setLoading(true);
    try {
      const input: CompanyClaimTypeInput = { name };
      await addCompanyClaimType(input);
      showToast(t('Claim type added'), 'success');
      setAddDialogOpen(false);
      resetAddForm();
      loadClaimTypes();
    } catch (err) {
      console.log(err);
      showToast(t('Failed to add claim type'), 'error');
    } finally { setLoading(false); }
  };

  const openDeleteDialog = async (ct: CompanyClaimTypeView) => {
    setLoading(true);
    try {
      const beneficiaries = await getNumberOfBeneficiariesOfClaimType(ct.id);
      setSelectedClaimType({ id: ct.id, name: ct.name, beneficiaries });
      if (beneficiaries > 0) {
        setDeleteBlockedMessage(t('payroll:This claim type has beneficiaries. Please unassign them before deleting.'));
      } else {
        setDeleteBlockedMessage(null);
      }
      setDeleteDialogOpen(true);
    } catch (err) {
      console.log(err);
      showToast(t('Failed to check beneficiaries'), 'error');
    } finally { setLoading(false); }
  };

  const handleDeleteClaimType = async () => {
    if (!selectedClaimType) return;
    if (selectedClaimType.beneficiaries > 0) return;
    setLoading(true);
    try {
      await deleteCompanyClaimType(selectedClaimType.id);
      showToast(t('Claim type deleted'), 'success');
      setDeleteDialogOpen(false);
      setSelectedClaimType(null);
      loadClaimTypes();
    } catch (err) {
      console.log(err);
      showToast(t('Failed to delete claim type'), 'error');
    } finally { setLoading(false); }
  };

  const totalPages = Math.max(1, Math.ceil(claimTypes.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  return (
    <PageLayout
      title={t('payroll:Claim Types')}
      icon={Receipt}
      accentColor="emerald"
      headerActions={(
        <Button
          variant="outline"
          iconName="PlusCircle"
          onClick={() => { resetAddForm(); setAddDialogOpen(true); }}
          data-testid="add-type-btn"
        >
          {t('payroll:Add Claim Type')}
        </Button>
      )}
    >
      <div className="flex flex-col flex-1 gap-5">
      {/* Summary Bar */}
      {claimTypes.length > 0 && (
        <div className="flex items-center gap-4 flex-wrap rounded-lg border border-border bg-card p-3">
          <span className="text-sm font-medium text-foreground">
            {t('Total')}: {formatter.format(totalCost)}
          </span>
          <span className="text-sm text-muted-foreground">
            {t('Showing')} {pagedClaimTypes.length} {t('of')} {claimTypes.length}
          </span>
        </div>
      )}

      {/* Empty State */}
      {!loading && claimTypes.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-6 text-center">
          <p className="font-semibold text-foreground">{t('No claim types configured')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('Click the button above to add your first claim type')}</p>
        </div>
      )}

      {/* Table */}
      {claimTypes.length > 0 && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <Table data-testid="settings-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('payroll:Claim Type')}</TableHead>
                <TableHead>{t('payroll:Number Of Beneficiaries')}</TableHead>
                <TableHead>{t('payroll:Employee Avg. Cost Range')}</TableHead>
                <TableHead>{t('payroll:Total Cost')}</TableHead>
                <TableHead className="w-[70px]">{t('common:Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedClaimTypes.map(ct => {
                const avg = ct.numberOfBeneficiaries ? (ct.totalCost / ct.numberOfBeneficiaries) : 0;
                return (
                  <TableRow key={ct.id}>
                    <TableCell className="font-semibold">{ct.name}</TableCell>
                    <TableCell>{ct.numberOfBeneficiaries}</TableCell>
                    <TableCell className="font-semibold">{formatter.format(avg)}</TableCell>
                    <TableCell className="font-semibold">{formatter.format(ct.totalCost || 0)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={String(t('common:Delete'))}
                        onClick={() => openDeleteDialog(ct)}
                      >
                        <AppIcon name="Trash2" size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {claimTypes.length > PAGE_SIZE && (
        <div className="flex justify-end">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent size="sm" data-testid="settings-form-dialog">
          <DialogHeader>
            <DialogTitle>{t('payroll:Add Claim Type')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Input
              label={t('payroll:Claim Type Name')}
              value={newClaimTypeName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewClaimTypeName(e.target.value)}
              autoFocus
              required
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); handleAddClaimType(); } }}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              {t('common:Cancel')}
            </Button>
            <Button onClick={handleAddClaimType} disabled={!newClaimTypeName.trim()} data-testid="save-btn">
              {t('common:Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteDialogOpen(false); setSelectedClaimType(null); } }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('payroll:Delete The Claim Type')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {deleteBlockedMessage ? (
              <div className="flex items-start gap-2 rounded-md border border-warning bg-warning/10 p-3 mb-2">
                <AppIcon name="AlertTriangle" size={18} className="text-warning mt-0.5" />
                <span className="text-sm text-foreground">{deleteBlockedMessage}</span>
              </div>
            ) : (
              <p className="text-sm text-foreground">
                {t('payroll:Are you sure you want to delete this claim type?')} {selectedClaimType?.name}
              </p>
            )}
            {selectedClaimType && selectedClaimType.beneficiaries > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                {t('Beneficiaries')}: {selectedClaimType.beneficiaries}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setSelectedClaimType(null); }}>
              {t('common:Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteClaimType}
              disabled={!!deleteBlockedMessage}
            >
              {t('common:Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/55">
          <Spinner size="lg" />
        </div>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};

export default ClaimsSettings;
